import { normalizeText } from '../helpers'
import type { BillSummary } from '../models'
import {
  canonicalStatusSlug,
  canonicalChamberSlug,
  defaultExplorerState,
  type ExplorerState,
} from './state'

export function filterBills(bills: BillSummary[], state: ExplorerState): BillSummary[] {
  const queryTerms = normalizeText(state.query)
    .split(/\s+/)
    .filter(Boolean)

  const wantedSubject = state.subject !== null ? normalizeText(state.subject) : null

  const filtered = bills.filter((bill) => {
    // Query: every term must appear in search_text
    if (!queryTerms.every((term) => bill.search_text.includes(term))) return false

    // Status filter
    if (state.status !== null && !matchesStatus(bill, state.status)) return false

    // Chamber filter
    if (state.chamber !== null && !matchesChamber(bill, state.chamber)) return false

    // Subject filter
    if (wantedSubject !== null && !bill.subjects.some((s) => normalizeText(s) === wantedSubject)) {
      return false
    }

    // withVotes filter
    if (state.withVotes !== null && bill.has_votes !== state.withVotes) return false

    return true
  })

  // Sort — do not mutate input
  const result = [...filtered]
  result.sort((a, b) => {
    if (state.sort === 'newest-filed') {
      return compareNullableDesc(a.filing_date, b.filing_date)
    }
    // 'recent-activity': latest_vote_date DESC then filing_date DESC
    const cmp = compareNullableDesc(a.latest_vote_date, b.latest_vote_date)
    if (cmp !== 0) return cmp
    return compareNullableDesc(a.filing_date, b.filing_date)
  })

  return result
}

export function searchResults(bills: BillSummary[], query: string, limit: number): BillSummary[] {
  const state: ExplorerState = {
    ...defaultExplorerState(),
    query,
  }
  return filterBills(bills, state).slice(0, limit)
}

function matchesStatus(bill: BillSummary, wanted: string): boolean {
  const normalizedStatus = normalizeText(bill.status)
  const slug = canonicalStatusSlug(wanted)
  if (slug === null) return false
  switch (slug) {
    case 'en-tramitacion':
      return normalizedStatus.includes('tramit')
    case 'publicado':
      return normalizedStatus.includes('publicado')
    default:
      return false
  }
}

function matchesChamber(bill: BillSummary, wanted: string): boolean {
  const normalizedChamber = normalizeText(bill.origin_chamber)
  const slug = canonicalChamberSlug(wanted)
  if (slug === null) return false
  switch (slug) {
    case 'senado':
      return normalizedChamber.includes('senado')
    case 'diputados':
      return normalizedChamber.includes('diput')
    default:
      return false
  }
}

/**
 * Compare two nullable strings in descending order.
 * Mirrors Rust's Option<String> ordering: None < Some(x), so in DESC, null sorts LAST.
 */
function compareNullableDesc(a: string | null, b: string | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a < b ? 1 : -1
}
